import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { DateRange, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { add, endOfMonth, endOfWeek, startOfMonth, startOfWeek, sub } from "date-fns";

interface DateRangePickerV2Props {
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  disableFuture?: boolean;
  inputLabel?: string;
  onSubmit?: (start: Date, end: Date) => void;
}

export const DateRangePickerV2 = ({
  defaultStartDate,
  defaultEndDate,
  disableFuture,
  inputLabel,
  onSubmit,
}: DateRangePickerV2Props) => {
  const [range, setRange] = React.useState<DateRange<Date>>([
    defaultStartDate || null,
    defaultEndDate || null,
  ]);

  const shortcutsItems: PickersShortcutsItem<DateRange<Date>>[] = [
    {
      label: "This Week",
      getValue: () => {
        const today = new Date();
        return [startOfWeek(today), endOfWeek(today)];
      },
    },
    {
      label: "Last Week",
      getValue: () => {
        const today = new Date();
        const prevWeek = sub(today, { weeks: 1 });
        return [startOfWeek(prevWeek), endOfWeek(prevWeek)];
      },
    },
    {
      label: "Last 7 Days",
      getValue: () => {
        const today = new Date();
        return [sub(today, { days: 7 }), today];
      },
    },
    {
      label: "Last Month",
      getValue: () => {
        const today = new Date();
        const endOfLastMonth = sub(startOfMonth(today), { days: 1 });
        return [startOfMonth(endOfLastMonth), endOfLastMonth];
      },
    },
    {
      label: "This Month",
      getValue: () => {
        const today = new Date();
        return [startOfMonth(today), endOfMonth(today)];
      },
    },
    {
      label: "Next Month",
      getValue: () => {
        const today = new Date();
        const startOfNextMonth = add(endOfMonth(today), { days: 1 });
        return [startOfNextMonth, endOfMonth(startOfNextMonth)];
      },
    },
    { label: "Reset", getValue: () => [defaultStartDate || null, defaultEndDate || null] },
    { label: "Clear", getValue: () => [null, null] },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        autoFocus={true}
        slots={{ field: SingleInputDateRangeField }}
        disableFuture={disableFuture}
        closeOnSelect={false}
        slotProps={{
          textField: {
            autoFocus: true,
            label: inputLabel || "Date Range",
            fullWidth: true,
          },
          shortcuts: {
            changeImportance: "set",
            items: shortcutsItems,
          },
          actionBar: { actions: ["cancel", "accept"] },
        }}
        onChange={(newRange) => {
          setRange(newRange);
        }}
        onAccept={(newRange, context) => {
          if (context.validationError[0] == null && context.validationError[1] == null) {
            if (onSubmit != null) {
              if (newRange[0] != null && newRange[1] != null) {
                onSubmit(newRange[0], newRange[1]);
              } else if (defaultStartDate != null && defaultEndDate != null) {
                setRange([defaultStartDate, defaultEndDate]);
                onSubmit(defaultStartDate, defaultEndDate);
              }
            }
          }
        }}
        calendars={1}
        name="Date Range"
        value={range}
      />
    </LocalizationProvider>
  );
};
